import { useAnalytics, withEventContext } from "@codat/analytics";
import {
  Button,
  FileUpload,
  Modal,
  ModalBody,
  QuestionIcon,
  Spinner,
  Typography,
  useTheme,
} from "@codat/orchard-ui";
import { useFileUploadService } from "api";
import { ApiError } from "api/shared";
import { AxiosError } from "axios";
import classNames from "classnames";
import { SourceType } from "model";
import { ParsedQuery } from "query-string";
import { useState } from "react";
import { scan } from "rxjs/operators";
import { getExtension, sourceTypeFileName } from "utils";

import ErrorFallback from "components/ErrorFallback";

import { useConfig } from "../../../contexts/ConfigContext";
import {
  applyUploadResult,
  canUpload,
  hasUploadErrors,
} from "./file-upload-state";
import styles from "./FileUpload.module.css";
import {
  FileListDisplay,
  FileUploadState,
  addFiles,
  isComplete,
  isUploading,
  removeFile,
  setUploading,
} from ".";

interface FileUploadModalProps {
  isOpen: boolean;
  sourceType: SourceType;
  companyId: string;
  query: ParsedQuery;
  onClose: () => void;
}

const allowedExtensions = new Set([
  "xls",
  "xlsx",
  "xlsb",
  "csv",
  "pdf",
  "doc",
  "docx",
  "ppt",
  "pptx",
  "jpeg",
  "jpg",
  "png",
]);
const allowedExtensionsString = Array.from(allowedExtensions).join(", ");
const maxFiles = 20;
const maxSize = 10 * 1024 * 1024;
const maxSizeString = "10 MB";

const FileUploadModalContent: React.FC<FileUploadModalProps> = (
  props: FileUploadModalProps
) => {
  const { isOpen, sourceType, companyId, query } = props;
  const analytics = useAnalytics();
  const { state: configState } = useConfig();
  const [fileState, setFileState] = useState<FileUploadState[]>([]);
  const [isAutoRedirecting, setIsAutoRedirecting] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [error, setError] = useState<AxiosError<ApiError> | undefined>();
  const { colors } = useTheme();

  const service = useFileUploadService();
  const clientName = configState?.data.clientName;

  const instructions: Record<SourceType, string | undefined> = {
    Accounting: configState.data?.pageConfiguration.fileUpload.accounting?.body,
    Banking: configState.data?.pageConfiguration.fileUpload.banking?.body,
    Commerce: undefined,
    Other:
      configState.data?.pageConfiguration.fileUpload.businessDocuments?.body,
  };

  const onFileSelect = (fileList: FileList) => {
    const filesArray = Array.from(fileList);

    analytics.sendEvent("Upload file picker - Chose files", {
      files: filesArray.map((f) => ({
        size: f.size,
        extension: getExtension(f.name),
      })),
    });

    setFileState(
      addFiles(fileState, filesArray, allowedExtensions, maxFiles, maxSize)
    );
  };

  const onFileDelete = (file: File) => {
    setFileState(removeFile(fileState, file));
  };

  const onUpload = () => {
    const files = fileState
      .filter((f) => f.state.kind === "ready")
      .map((f) => f.file);
    const initialFileState = setUploading(fileState);
    setFileState(initialFileState);
    service
      .uploadFiles(companyId, query, sourceType, files)
      .pipe(
        scan(
          (acc, fileUploadResult) => applyUploadResult(acc, fileUploadResult),
          initialFileState
        )
      )
      .subscribe({
        next: (state) => {
          setFileState(state);

          // if everything successfully completed then redirect to success
          if (isComplete(state)) {
            setIsAutoRedirecting(true);
            service.successRedirect(companyId, query, sourceType);
          }
        },
        error: (apiError) => {
          setError(apiError);
        },
      });
  };

  const onClose = () => {
    setShowWarning(false);
    setFileState([]);
    setIsAutoRedirecting(false);
    analytics.sendEvent("Confirmed close");
    props.onClose();
  };

  const buttonState: {
    element: JSX.Element;
    disabled: boolean;
    onClick?: () => void;
    className?: string;
  } = (() => {
    if (isUploading(fileState) || isAutoRedirecting) {
      return {
        element: (
          <div className={styles.uploadingButtonContent}>
            <Spinner />
            <div>Uploading</div>
          </div>
        ),
        disabled: true,
        className: styles.uploadingButton,
      };
    } else if (isComplete(fileState)) {
      return {
        element: <div>Next</div>,
        disabled: false,
        onClick: () => service.successRedirect(companyId, query, sourceType),
      };
    } else if (hasUploadErrors(fileState)) {
      return { element: <div>Next</div>, disabled: true };
    } else {
      return {
        element: <div>Upload files</div>,
        disabled: !canUpload(fileState),
        onClick: onUpload,
      };
    }
  })();

  if (error) {
    return <ErrorFallback error={error} />;
  }

  if (!isOpen) {
    return <></>;
  }

  return showWarning ? (
    <Modal
      onClose={() => {
        /* Cannot be closed */
      }}
    >
      <ModalBody
        className={styles.warningModalBody}
        title={"Abandon file upload?"}
        size="small"
        headerIcon={QuestionIcon}
        headerIconColor={colors["color-ui-negative"]}
        callToAction={{
          type: "buttons",
          buttons: [
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => {
                analytics.sendEvent("Cancelled close");
                setShowWarning(false);
              },
            },
            { label: "Confirm", variant: "primary", onClick: onClose },
          ],
          centerAlign: true,
        }}
        centerAlign
      >
        <Typography as="h3" className={styles.warningModalText}>
          {`Your files have not been sent to `}
          <span className={styles.clientName}>{clientName}</span>
        </Typography>
      </ModalBody>
    </Modal>
  ) : (
    <Modal
      onClose={() => {
        /* Cannot be closed */
      }}
    >
      <ModalBody
        data-testid="file-upload-modal"
        className={styles.modalBody}
        subtitleClassName={styles.modalHelpText}
        size="medium"
        title={`Upload your ${sourceTypeFileName[sourceType]}`}
        subtitle={instructions[sourceType]}
        onClose={() => {
          analytics.sendEvent("Clicked close");
          setShowWarning(true);
        }}
      >
        <FileUpload id="file-upload" onFileSelect={onFileSelect} multiple />
        <Typography
          as="p"
          variant="body_sm"
          className={classNames(styles.modalHelpText, styles.fileInfoText)}
        >
          Maximum files: {maxFiles} • Up to {maxSizeString}
          <br />
          File types: {allowedExtensionsString}
        </Typography>
        <FileListDisplay
          current={fileState}
          maxFiles={maxFiles}
          onDelete={onFileDelete}
        />
        {hasUploadErrors(fileState) && (
          <Typography
            as="p"
            className={styles.uploadErrorWarning}
            data-testid="upload-failed-message"
          >
            There was an issue when uploading some files. Delete these to
            continue.
          </Typography>
        )}
        <Button
          className={classNames(styles.uploadButton, buttonState.className)}
          onClick={buttonState.onClick}
          disabled={buttonState.disabled}
          data-testid="file-upload-button"
        >
          {buttonState.element}
        </Button>
      </ModalBody>
    </Modal>
  );
};

export const FileUploadModal = withEventContext<
  FileUploadModalProps & JSX.IntrinsicAttributes
>(FileUploadModalContent, "File upload modal");
